import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import {
  type CreateSessionRequest,
  type SessionFileContribution,
  type SessionRecord,
} from "@/shared/domain/sessions";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseCreateSessionFromFilesVariables {
  request: CreateSessionRequest;
  /**
   * Whole-file contributions to attach after the session is created. Empty
   * is allowed: the session is created with no contributions and no second
   * call is made.
   */
  files: SessionFileContribution[];
  /**
   * Org the session is created in, sent as the `X-Roboto-Org-Id` header on
   * the create call. The attach and rollback-delete calls identify the
   * session by ID and the server authorizes them against the org that owns
   * it, so they carry no org.
   */
  orgId: string;
}

/**
 * Create a session and attach `files` to it in a follow-up call. If the
 * attach fails, a best-effort rollback deletes the just-created session and
 * the attach error (not the rollback error) is surfaced; when the rollback
 * delete itself fails, the failure is reported to monitoring and the orphan
 * session remains on the server. On success the returned record seeds the
 * session's detail cache and the session lists are invalidated.
 *
 * One mutation wraps both service calls so the rollback can span them.
 */
export function useCreateSessionFromFiles() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<SessionRecord, Error, UseCreateSessionFromFilesVariables>({
    mutationFn: async ({ request, files, orgId }) => {
      const session = await sessionService.createSession(request, { orgId });

      if (files.length === 0) {
        return session;
      }

      try {
        return await sessionService.addFilesToSession(
          session.session_id,
          files,
        );
      } catch (error) {
        // Roll back by deleting the session; it was created by this same
        // mutation, so the delete destroys nothing the user had before.
        try {
          await sessionService.deleteSession(session.session_id);
        } catch (rollbackError) {
          // Swallow a rollback failure (report it to monitoring); the
          // original attach error below still propagates.
          ErrorMonitoringService.captureError(rollbackError);
        }
        throw error;
      }
    },
    onSuccess: (record) => {
      queryClient.setQueryData(
        sessionQueryKeys.detail(record.session_id),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: sessionQueryKeys.lists(),
      });
    },
  });
}
